import { useMutationNotification } from "@/composables/logger/mutationNotification";
import {
  deleteHousings,
  restoreHousings
} from "@/models/equipment/housings/predicates";
import type {
  Housings,
  HousingsQueryVariables,
  Housings_Insert_Input
} from "gql/api";
import {
  useHousingsQuery,
  useDeleteHousingsMutation,
  useRestoreHousingsMutation,
  useUpsertHousingsMutation
} from "gql/api";
import { v4 } from "uuid";
import type { ComputedRef } from "vue";
import { inject, computed } from "vue";
import { useEquipment } from "@/composables/equipment/baseEquipment";
import type { UseEquipment } from "@/composables/equipment/equipmentTypes";
import { housingTableColumns } from "./housingTableColumns";
import useUserInfo, {
  KUWAIT_OIL_COMPANY_ID,
  ECOPETROL_IDS
} from "@/composables/auth/useUserInfo";
import { sizingFormKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";

export type UseHousings = Partial<UseEquipment<Housings>> & {
  isValid: ComputedRef<boolean>;
  handleUpsert(): Promise<void>;
  handleRowEditInit(): void;
};

// If pumpHousings is not empty, use that instead of querying for housings
export function useHousings(
  isAdmin: boolean,
  pumpId: string,
  pumpHousings: ComputedRef<Housings[]>
): UseHousings {
  const { registerResultToast } = useMutationNotification();
  const user = useUserInfo();

  const pumpHousingsExist = computed(() => pumpHousings.value.length > 0);

  const defaultHousingVars: HousingsQueryVariables = {
    where: {
      pump_id: { _eq: pumpId }
    }
  };
  const adminHousingVars: HousingsQueryVariables = {
    where: {
      pump_id: { _eq: pumpId }
    }
  };

  const kocHousingVars: HousingsQueryVariables = {
    where: {
      pump_id: { _eq: pumpId },
      _or: [
        { include_koc_timestamp: { _is_null: false } },
        { only_koc_timestamp: { _is_null: false } }
      ]
    }
  };

  const ecopetrolHousingVars: HousingsQueryVariables = {
    where: {
      pump_id: { _eq: pumpId },
      _or: [
        { include_ecopetrol_timestamp: { _is_null: false } },
        { only_ecopetrol_timestamp: { _is_null: false } }
      ]
    }
  };
  const baseEquipment = useEquipment<Housings>(isAdmin);

  const {
    data,
    executeQuery: refetch,
    stale: isStale
  } = useHousingsQuery({
    variables: computed(() => {
      if (user.is_koc_customer) return kocHousingVars;
      if (user.is_ecopetrol_customer) return ecopetrolHousingVars;
      if (user.is_admin) return adminHousingVars;
      return defaultHousingVars;
    }),
    pause: pumpHousingsExist
  });

  const sizingForm = inject(sizingFormKey, null);

  const forceKOC = computed(
    () =>
      sizingForm?.data.value.customer?.customer_erp_id === KUWAIT_OIL_COMPANY_ID
  );

  const forceECOPETROL = computed(() =>
    ECOPETROL_IDS.find(
      id => id === sizingForm?.data.value.customer?.customer_erp_id
    )
  );
  // if housings passed as prop use that
  // if housings prop is empty, use from query above
  // filter out deleted based on toggle value
  const housings = computed(() => {
    const localHousings = pumpHousingsExist.value
      ? pumpHousings.value
      : data.value?.housings || [];
    if (forceKOC.value) {
      return localHousings
        .filter(
          housing =>
            Boolean(housing.deleted_at) === baseEquipment.showDeleted.value
        )
        .filter(
          housing =>
            forceKOC.value &&
            (Boolean(housing.include_koc_timestamp) ||
              Boolean(housing.only_koc_timestamp))
        );
    } else if (forceECOPETROL.value) {
      return localHousings
        .filter(
          housing =>
            Boolean(housing.deleted_at) === baseEquipment.showDeleted.value
        )
        .filter(
          housing =>
            forceECOPETROL.value &&
            (Boolean(housing.include_ecopetrol_timestamp) ||
              Boolean(housing.only_ecopetrol_timestamp))
        );
    } else {
      const housings = localHousings.filter(
        housing =>
          Boolean(housing.deleted_at) === baseEquipment.showDeleted.value
      );
      if (sizingForm) {
        return housings.filter(
          housing =>
            !housing.only_ecopetrol_timestamp && !housing.only_koc_timestamp
        );
      }
      return housings;
    }
  });

  // Overwrite event handlers

  baseEquipment.handleRowCreate = () => {
    baseEquipment.editingRows.value.push({
      id: v4(),
      stage_count: null,
      housing_number: null,
      length_inches: null,
      weight_pounds: null,
      pump_id: pumpId,
      created_at: "now()",
      updated_at: "now()",
      deleted_at: null
    });
  };

  baseEquipment.handleEquipmentCancelled = () => {
    baseEquipment.isEditingAll.value = false;
    baseEquipment.editingRows.value = [];
  };

  const handleRowEditInit = () => {
    baseEquipment.flagged.value = [];
    baseEquipment.isEditingAll.value = true;
    baseEquipment.editingRows.value = housings.value
      ? housings.value.map(housing => ({ ...housing }))
      : [];
  };

  const { executeMutation: executeUpsertHousings } =
    useUpsertHousingsMutation();
  const { executeMutation: executeDeleteHousing } = useDeleteHousingsMutation();
  const { executeMutation: executeRestoreHousing } =
    useRestoreHousingsMutation();

  async function handleUpsert(): Promise<void> {
    const editedHousings = baseEquipment.editingRows.value.map(housing => {
      delete housing.__typename;
      delete housing.author_id;
      return {
        ...housing
      };
    }) as Housings_Insert_Input[];

    const result = executeUpsertHousings({ objects: editedHousings });
    registerResultToast("Housing Update", result);
    await Promise.allSettled([result]);

    baseEquipment.handleEquipmentCancelled();

    refetch();
  }

  const { defineVisibleColumns, defineMutationHandlers } = baseEquipment;

  const { defineRestore, defineDelete } = defineMutationHandlers(refetch, []);

  const deleteHousing = (ids: string[]) =>
    executeDeleteHousing({ where: deleteHousings(ids) });

  const restoreHousing = (ids: string[]) =>
    executeRestoreHousing({ where: restoreHousings(ids) });

  const isValid = computed(() =>
    baseEquipment.editingRows.value.every(
      ({ housing_number, stage_count }) =>
        housing_number && stage_count && housing_number > 0 && stage_count > 0
    )
  );

  return {
    title: "Housings",
    searchPlaceholder: "Search by Manufacturer or Series Model...",
    columns: defineVisibleColumns(housingTableColumns),
    filteredRows: housings as ComputedRef<Housings[]>,
    handleUpsert,
    handleDelete: defineDelete(deleteHousing),
    handleRestore: defineRestore(restoreHousing),
    handleRowEditInit,
    isValid,
    isStale,
    totalCount: computed(() => housings.value?.length),
    ...baseEquipment
  };
}
